import { GetListParams } from 'react-admin';
import { CustomDataProvider } from '../../../providers/CustomDataProvider';
import { baseUrl, httpClient } from '../../../providers/AppDataProvider';

export class ActivityTasksDataProvider implements CustomDataProvider {
  // eslint-disable-next-line class-methods-use-this
  async getList(resource: string, params: GetListParams): Promise<any> {
    if (resource !== 'activity-task')
      return Promise.reject(new Error('Unsupported getList resource'));

    const { page = 1, perPage = 10 } = params.pagination || {};
    const { filter = {}, meta = {} } = params;
    const sort = JSON.stringify(params.sort);

    // 构建查询参数
    const searchParams = new URLSearchParams({
      sort,
      page: page.toString(),
      size: perPage.toString(),
      query: JSON.stringify(filter),
      ...meta,
    });

    // 构建请求 URL
    const url = `${baseUrl}/activity/adminGetAllTasks?${searchParams.toString()}`;

    try {
      const { json } = await httpClient(url);

      // 根据您的数据结构处理数据
      // 这里假设您的 API 返回格式可能不同，需要特殊处理
      let rows: any[] = [];
      let total = 0;

      // 根据实际 API 响应结构调整
      if (json?.data) {
        rows = json.data;
        total = json.total || json.data.length;
      } else if (json?.tasks) {
        rows = json.tasks;
        total = json.total || json.tasks.length;
      } else if (Array.isArray(json)) {
        rows = json;
        total = json.length;
      } else {
        // 默认处理
        rows = json?.list ?? json?.items ?? json?.records ?? [];
        total = json?.total ?? json?.count ?? rows.length;
      }

      // 客户端筛选：支持 id、categoryId、startDate、endDate
      const { id: idFilter, categoryId: categoryIdFilter, startDate: startDateFilter, endDate: endDateFilter } = (filter ?? {}) as any;

      const parseDateToMs = (value: any): number | null => {
        if (value == null || value === '') return null;
        if (typeof value === 'number') {
          // 支持秒/毫秒两种时间戳
          return value > 1e12 ? value : value * 1000;
        }
        const ms = new Date(value).getTime();
        return Number.isNaN(ms) ? null : ms;
      };

      const startMs = parseDateToMs(startDateFilter);
      let endMs = parseDateToMs(endDateFilter);
      if (endMs != null) {
        const endDateObj = new Date(endMs);
        if (
          endDateObj.getHours() === 0 &&
          endDateObj.getMinutes() === 0 &&
          endDateObj.getSeconds() === 0 &&
          endDateObj.getMilliseconds() === 0
        ) {
          endDateObj.setHours(23, 59, 59, 999);
          endMs = endDateObj.getTime();
        }
      }

      rows = rows.filter((row: any) => {
        if (idFilter != null && `${row?.id}` !== `${idFilter}`) return false;
        if (categoryIdFilter != null && `${row?.categoryId}` !== `${categoryIdFilter}`) return false;
        const createdAtMs = new Date(row?.createdAt || 0).getTime();
        if (startMs != null && createdAtMs < startMs) return false;
        if (endMs != null && createdAtMs > endMs) return false;
        return true;
      });
      total = rows.length;
      // 添加数据量检查，避免处理过多数据
      if (rows.length > 10000) {
        console.warn('Large dataset detected:', rows.length, 'items. Consider server-side pagination.');
      }

      //data 按照创建时间倒序
      rows.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateB - dateA;
      });

      // 确保每条记录都有 id，并限制处理的数据量
      const allData = rows.slice(0, 10000).map((r: any, i: number) => (r?.id != null ? r : { id: `${i}`, ...r }));

      // 前端分页处理
      const startIndex = (page - 1) * perPage;
      const endIndex = startIndex + perPage;
      const data = allData.slice(startIndex, endIndex);
      return { data, total: Math.min(total, 10000) };
    } catch (error) {
      console.error('ActivityTasksDataProvider getList error:', error);
      throw error;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  async create(resource: string, params: any): Promise<any> {
    // 仅拦截 activity/tasks 列表对应的创建
    if (resource !== 'activity-task')
      return Promise.reject(new Error('Unsupported Created resource'));
    let url = `${baseUrl}/activity/createTask`;
    const payload = params?.data ?? {};
    if (payload.localType === 'ACCUMULATED_MEME_TRADING_VOLUME') {
      url = `${baseUrl}/activity/createAccumulatedMEMETradingVolumeTask`;
    }

    if (payload.localType === 'CONSECUTIVE_CHECKIN') {
      url = `${baseUrl}/activity/createConsecutiveCheckinTask`;
    }

    // 🔍 检查并转换 startDate / endDate
    const convertToSeconds = (time?: number | null) => {
      if (!time) return time;
      // 如果是毫秒级 (13位)，转成秒级
      return time > 1e12 ? Math.floor(time / 1000) : time;
    };

    if (payload.startDate) {
      payload.startDate = convertToSeconds(payload.startDate);
    }
    if (payload.endDate) {
      payload.endDate = convertToSeconds(payload.endDate);
    }
    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    return { data: json?.data ?? json };
  }

  // eslint-disable-next-line class-methods-use-this
  async update(resource: string, params: any): Promise<any> {
    if (resource !== 'activity-task')
      return Promise.reject(new Error('Unsupported update resource'));
    // 如果是ACCUMULATED_MEME_TRADING为前缀，则删除taskIdentifier字段
    const prefix = 'ACCUMULATED_MEME_TRADING';
    if (params.data.taskIdentifier.startsWith(prefix)) {
      // 3. 满足条件则删除该字段
      delete params.data.taskIdentifier;
    }
    const url = `${baseUrl}/activity/updateTask`;
    const payload = params?.data ?? {};
    console.log('ActivityTasksDataProvider update payload:', params);

    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    return { data: json?.data ?? json };
  }

  // eslint-disable-next-line class-methods-use-this
  async delete(resource: string, params: any): Promise<any> {
    if (resource !== 'activity-task')
      return Promise.reject(new Error('Unsupported delete resource'));

    const url = `${baseUrl}/activity/deleteTask`;
    const payload = { taskId: params?.id ?? params?.previousData?.id };

    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    return { data: json ?? { id: payload.taskId } };
  }
}

// 创建实例并导出
export const activityTasksDataProvider = new ActivityTasksDataProvider();
